// Master-key derivation.
//
// The master password is stretched with Argon2id (memory-hard, GPU/ASIC
// resistant) into a 256-bit master key. That master key is NEVER used directly
// to encrypt data: HKDF expands it into purpose-specific subkeys, so a subkey
// leaking can't be used outside its purpose. WebCrypto has no Argon2, hence the
// hash-wasm dependency; HKDF and AES are native WebCrypto.

import { argon2id } from "hash-wasm";
import { utf8, type Bytes } from "./encoding";

export interface KdfParams {
  algorithm: "argon2id";
  iterations: number;
  memoryKiB: number;
  parallelism: number;
}

// Tuned for an interactive in-browser unlock (~64 MiB, a few hundred ms on a
// typical laptop). Stored per-user so parameters can be raised over time
// without breaking existing vaults.
export const DEFAULT_KDF_PARAMS: KdfParams = {
  algorithm: "argon2id",
  iterations: 3,
  memoryKiB: 65536,
  parallelism: 1,
};

const MASTER_KEY_BYTES = 32;

/** Stretch the master password + salt into the 256-bit master key. */
export async function deriveMasterKey(
  password: string,
  salt: Bytes,
  params: KdfParams = DEFAULT_KDF_PARAMS,
): Promise<Bytes> {
  const key = await argon2id({
    password,
    salt,
    iterations: params.iterations,
    memorySize: params.memoryKiB,
    parallelism: params.parallelism,
    hashLength: MASTER_KEY_BYTES,
    outputType: "binary",
  });
  // Copy into an ArrayBuffer-backed view for WebCrypto (hash-wasm's return type
  // is the wider ArrayBufferLike).
  return new Uint8Array(key);
}

/**
 * HKDF-expand the master key into raw bytes for a non-key purpose (the auth
 * key sent to the server at sign-in). Domain-separated from the "enc" subkey,
 * so knowing the auth key reveals nothing about the encryption key.
 */
export async function deriveSubBytes(
  masterKey: Bytes,
  salt: Bytes,
  info: string,
): Promise<Bytes> {
  const base = await crypto.subtle.importKey("raw", masterKey, "HKDF", false, [
    "deriveBits",
  ]);
  const bits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt, info: utf8(info) },
    base,
    256,
  );
  return new Uint8Array(bits);
}

/**
 * HKDF-expand the master key into a purpose-bound AES-256-GCM key.
 * `info` (e.g. "enc") domain-separates subkeys derived from the same master key.
 */
export async function deriveSubKey(
  masterKey: Bytes,
  salt: Bytes,
  info: string,
): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", masterKey, "HKDF", false, [
    "deriveKey",
  ]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt, info: utf8(info) },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}
